import React, { ReactNode, useRef, isValidElement } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popup } from '@progress/kendo-react-popup';
import cn from 'clsx';
import styles from './styles.module.scss';
import Tooltip from '@components-clean/core/tooltip';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useBoolean, useOnClickOutside } from '@hooks/index';

export interface GridMenuItemProps {
    title: string;
    'data-testid': string;
    icon?: IconProp;
    iconFontSize?: string;
    customIcon?: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    children?: ReactNode;
    ignoreReadOnlyContext?: boolean;
    isLastMenuItem?: boolean;
    iconColor?: string;
    stopPropagation?: boolean;
}

const GridMenuItem = ({
    children,
    icon,
    iconFontSize = '18px',
    customIcon,
    title,
    onClick,
    disabled,
    'data-testid': testid,
    ignoreReadOnlyContext,
    isLastMenuItem,
    iconColor,
    stopPropagation,
}: GridMenuItemProps) => {
    const anchor = useRef<HTMLButtonElement | null>(null);
    const menuItemRef = useRef<HTMLDivElement | null>(null);
    const isOpen = useBoolean(false);
    const { readOnly } = useReadOnlyContext();
    const [anchorAlign, setAnchorAlign] = React.useState<{
        horizontal: 'left' | 'center' | 'right';
        vertical: 'top' | 'center' | 'bottom';
    }>({ horizontal: 'right', vertical: 'bottom' });
    const [popupAlign, setPopupAlign] = React.useState<{
        horizontal: 'left' | 'center' | 'right';
        vertical: 'top' | 'center' | 'bottom';
    }>({ horizontal: 'right', vertical: 'top' });

    useOnClickOutside(menuItemRef, () => {
        setTimeout(() => isOpen.setFalse(), 300);
    });

    const recalculateDirection = React.useCallback(() => {
        if (!anchor.current || !menuItemRef.current) return;

        const anchorRect = anchor.current.getBoundingClientRect();
        const popupHeight = menuItemRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;

        if (spaceBelow < popupHeight && spaceAbove > spaceBelow) {
            setAnchorAlign({ horizontal: 'right', vertical: 'top' });
            setPopupAlign({ horizontal: 'right', vertical: 'bottom' });
        } else {
            setAnchorAlign({ horizontal: 'right', vertical: 'bottom' });
            setPopupAlign({ horizontal: 'right', vertical: 'top' });
        }
    }, []);

    React.useEffect(() => {
        if (!isOpen.value || !menuItemRef.current) return;

        const observer = new ResizeObserver(recalculateDirection);
        observer.observe(menuItemRef.current);

        return () => observer.disconnect();
    }, [isOpen.value, recalculateDirection]);

    const updatedChild =
        children &&
        isValidElement(children) &&
        React.cloneElement(children, {
            closeMenu: () => isOpen.setFalse(),
        } as { closeMenu: () => void });

    return (
        <>
            <Tooltip position={isLastMenuItem ? 'left' : 'top'} parentTitle>
                <button
                    title={title}
                    aria-label={title}
                    type="button"
                    onClick={(e) => {
                        if (stopPropagation) e.stopPropagation();
                        (onClick ?? isOpen.toggle)();
                    }}
                    ref={anchor}
                    className={cn('k-bg-transparent k-border-0 k-p-0', styles.button, {
                        'k-cursor-pointer': !disabled,
                    })}
                    disabled={disabled || (readOnly && !ignoreReadOnlyContext)}
                    data-testid={testid}
                >
                    {customIcon ? (
                        <span
                            className={cn(styles.icon, {
                                [styles.active]: isOpen.value,
                                [styles.disabled]: disabled || readOnly,
                            })}
                            title={title}
                        >
                            {customIcon}
                        </span>
                    ) : (
                        icon && (
                            <FontAwesomeIcon
                                aria-label={title}
                                icon={icon}
                                className={cn(styles.icon, {
                                    [styles.active]: isOpen.value,
                                    [styles.disabled]: disabled || readOnly,
                                })}
                                style={{ fontSize: iconFontSize, color: iconColor }}
                            />
                        )
                    )}
                </button>
            </Tooltip>
            <Popup
                anchor={anchor.current}
                show={isOpen.value}
                anchorAlign={anchorAlign}
                popupAlign={popupAlign}
                popupClass={styles.popup}
            >
                <div ref={menuItemRef}>{updatedChild}</div>
            </Popup>
        </>
    );
};

export default GridMenuItem;
